import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import BuyMerchModal from "../components/BuyMerchModal";
import { ShoppingBag, Crown } from "lucide-react";
import { useAuth } from "../context/AuthContext";

function formatDollar(cents) {
  return "$" + (cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

const CATEGORY_META = {
  tshirt: "Apparel",
  hat: "Headwear",
  other: "Merch",
};

const BRAND_META = {
  bdu: {
    key: "bdu",
    name: "Black Dollars United",
    tagline: "The Fund's flagship line — gold on obsidian.",
    accent: "text-bdu-gold",
    border: "border-bdu-gold/40",
    chip: "bg-bdu-gold/10 text-bdu-gold border-bdu-gold/50",
    eyebrow: "BDU · Signature Line",
  },
  rise_ii_power: {
    key: "rise_ii_power",
    name: "Rise II Power",
    tagline: "Greg L Bradford's parallel apparel line carrying the Trillion Dollar Spending Power message.",
    accent: "text-bdu-cream",
    border: "border-bdu-cream/30",
    chip: "bg-bdu-cream/5 text-bdu-cream border-bdu-cream/40",
    eyebrow: "Rise II Power · Trillion Dollar Spending Power",
  },
};

function BrandSection({ brandKey, items, onSelect }) {
  const meta = BRAND_META[brandKey] || BRAND_META.bdu;
  if (!items.length) return null;
  return (
    <section className="mb-20" data-testid={`shop-brand-${brandKey}`}>
      <div className={`border-t ${meta.border} pt-8 mb-8 flex items-end justify-between flex-wrap gap-4`}>
        <div className="max-w-2xl">
          <div className={`label-eyebrow ${meta.accent}`} style={{ fontSize: "0.6rem" }}>
            {meta.eyebrow}
          </div>
          <h2 className={`mt-3 font-serif text-3xl sm:text-4xl leading-tight ${meta.accent}`}>
            {meta.name}
          </h2>
          <p className="mt-2 text-sm text-bdu-cream/60">{meta.tagline}</p>
        </div>
        <div className="text-xs uppercase tracking-widest text-bdu-cream/40 tabular-nums">
          {items.length} {items.length === 1 ? "piece" : "pieces"}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={`shop-grid-${brandKey}`}>
        {items.map((m) => (
          <article
            key={m.id}
            className="group border border-bdu-gold/10 bg-bdu-surface overflow-hidden flex flex-col"
            data-testid={`merch-card-${m.id}`}
          >
            <div className="relative aspect-square bg-bdu-bg overflow-hidden">
              {m.image_url ? (
                <img
                  src={m.image_url}
                  alt={m.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              ) : null}
              <div className="absolute top-4 left-4 flex flex-col gap-1.5 items-start">
                <span className={`text-[0.6rem] uppercase tracking-[0.24em] px-2.5 py-1 border backdrop-blur-sm ${meta.chip}`}>
                  {meta.name}
                </span>
                <span className="text-[0.6rem] uppercase tracking-[0.24em] px-2.5 py-1 border border-bdu-gold/40 text-bdu-cream/70 bg-black/50 backdrop-blur-sm">
                  {CATEGORY_META[m.category] || "Merch"}
                </span>
              </div>
            </div>
            <div className="p-6 flex-1 flex flex-col">
              <h3 className="font-serif text-xl text-bdu-cream leading-tight">{m.name}</h3>
              <p className="mt-2 text-sm text-bdu-cream/60 line-clamp-2 flex-1">{m.description}</p>
              <div className="mt-5 flex items-center justify-between">
                <div className="font-serif text-2xl text-bdu-gold tabular-nums" data-testid={`merch-price-${m.id}`}>
                  {formatDollar(m.price_cents)}
                </div>
                <button
                  onClick={() => onSelect(m)}
                  className="btn-gold"
                  data-testid={`merch-shop-btn-${m.id}`}
                >
                  Shop
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default function Shop() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [tier, setTier] = useState(null);

  useEffect(() => {
    api.get("/merchandise").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) { setTier(null); return; }
    api.get("/referrals/me").then((r) => setTier(r.data?.current_tier || null)).catch(() => {});
  }, [user]);

  const { bdu, r2p } = useMemo(() => {
    const b = [];
    const r = [];
    for (const m of items) {
      if (m.brand === "rise_ii_power") r.push(m);
      else b.push(m);
    }
    return { bdu: b, r2p: r };
  }, [items]);

  return (
    <div className="grain min-h-screen">
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-12" data-testid="shop-hero">
        <div className="label-eyebrow flex items-center gap-2"><ShoppingBag size={14} /> Fund Merchandise</div>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-bdu-cream tracking-tight max-w-3xl">
            Wear the movement. <span className="italic text-bdu-gold">Fund the fund.</span>
          </h1>
        </div>
        <p className="mt-6 text-bdu-cream/70 max-w-2xl leading-relaxed">
          Two lines, one mission. The Black Dollars United signature collection sits alongside Greg L Bradford's Rise II Power apparel line.
          Proceeds from every sale go directly into the Trillion Dollar Spending Power Fund's Citizen Trust Bank account.
        </p>

        {tier && tier.merch_discount_pct > 0 && (
          <div className="mt-8 inline-flex items-center gap-3 border border-bdu-gold/50 bg-bdu-gold/10 px-5 py-3" data-testid="shop-ambassador-discount">
            <Crown size={18} className="text-bdu-gold shrink-0" />
            <div className="text-sm text-bdu-cream">
              <span className="text-bdu-gold font-serif text-lg">{tier.merch_discount_pct}% off automatically applied</span>
              <span className="text-bdu-cream/60"> — {tier.name} perk. Discount lands at checkout on any line.</span>
            </div>
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
        {loading ? (
          <div className="text-bdu-cream/50 py-16 text-center">Loading merchandise...</div>
        ) : items.length === 0 ? (
          <div className="bdu-card text-center py-16" data-testid="shop-empty">
            <div className="font-serif text-3xl text-bdu-cream">Drop coming soon.</div>
            <p className="mt-3 text-bdu-cream/60">Our team is finalizing the first BDU merch drop. Check back — or subscribe to be notified.</p>
          </div>
        ) : (
          <>
            <BrandSection brandKey="bdu" items={bdu} onSelect={setSelected} />
            <BrandSection brandKey="rise_ii_power" items={r2p} onSelect={setSelected} />
          </>
        )}
      </section>

      <BuyMerchModal
        open={!!selected}
        item={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
